/**
 * @fileoverview
 * Why Choose Aideco section for the homepage.
 *
 * Layout matching live aideco.us:
 * - Eyebrow + large heading, left-aligned
 * - Left column: Two stacked architectural photos
 * - Right column: One large photo with a floating white overlay card
 *   containing dot navigation, feature icon, title, and description
 * - Full-width descriptive paragraph below the grid
 */

"use client";

import { useState, useCallback, useRef, useMemo } from "react";
import Image from "next/image";
import { useLingui } from "@lingui/react/macro";
import { useMountEffect } from "../../../../hooks/use-mount-effect";
import { SectionShell, SectionContainer, SectionEyebrow } from "../primitives";

export function WhyChooseSection() {
  const { t } = useLingui();

  const features = useMemo(() => [
    {
      id: "exterior",
      title: t`Exterior Rendering`,
      description: t`Generate photorealistic exterior views from your CAD elevations and site plans with one click.`,
      icon: "/images/marketing/688b542a919fe788e712443c_1.svg",
    },
    {
      id: "interior",
      title: t`Interior Rendering`,
      description: t`Transform floor plans into stunning interior visualizations complete with furniture, lighting, and materials.`,
      icon: "/images/marketing/688b54489302e515a0b0203d_2.svg",
    },
    {
      id: "styles",
      title: t`Multiple Style Options`,
      description: t`Choose from photorealistic, clay model, sketch, or 3D visualization styles to match your presentation needs.`,
      icon: "/images/marketing/688b545b834a09a6fbfa5110_3.svg",
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t]);

  const [activeSlide, setActiveSlide] = useState(0);
  const isPausedRef = useRef(false);
  const autoplayIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const goToSlide = useCallback((index: number) => {
    setActiveSlide(index);
  }, []);

  const goToNextSlide = useCallback(() => {
    setActiveSlide((prev) => (prev + 1) % features.length);
  }, [features.length]);

  const goToPrevSlide = useCallback(() => {
    setActiveSlide((prev) => (prev - 1 + features.length) % features.length);
  }, [features.length]);

  // Autoplay: advance slides every 5 seconds
  useMountEffect(() => {
    autoplayIntervalRef.current = setInterval(() => {
      if (!isPausedRef.current) {
        goToNextSlide();
      }
    }, 5000);

    return () => {
      if (autoplayIntervalRef.current) {
        clearInterval(autoplayIntervalRef.current);
      }
    };
  });

  const handleMouseEnter = useCallback(() => {
    isPausedRef.current = true;
  }, []);

  const handleMouseLeave = useCallback(() => {
    isPausedRef.current = false;
  }, []);

  const activeFeature = features[activeSlide];

  return (
    <SectionShell id="about" ariaLabel={t`Why choose section`} noPadding className="py-6 sm:py-14 lg:py-20">
      <SectionContainer>
        {/* Eyebrow + Heading above the grid */}
        <SectionEyebrow label={t`Why choose Aideco?`} align="left" />
        <h2 className="mb-8 text-2xl font-bold leading-tight text-gray-900 sm:text-3xl md:text-4xl lg:text-5xl">
          {t`Turn AutoCAD drawings into stunning architectural renders in minutes.`}
        </h2>

        {/* Two-column image grid */}
        <div className="grid gap-4 lg:grid-cols-2 lg:gap-6">
          {/* Left Column — Two stacked images */}
          <div className="flex flex-col gap-4">
            <div className="relative aspect-[4/3] w-full overflow-hidden rounded-xl">
                <Image
                  alt={t`Exterior architectural rendering example`}
                className="object-cover"
                fill
                loading="lazy"
                quality={85}
                sizes="(max-width: 768px) 100vw, 50vw"
                src="/images/marketing/6863845b492e758711453c7d_bg.webp"
              />
            </div>
            <div className="relative aspect-[4/3] w-full overflow-hidden rounded-xl">
                <Image
                  alt={t`Interior architectural rendering example`}
                className="object-cover"
                fill
                loading="lazy"
                quality={85}
                sizes="(max-width: 768px) 100vw, 50vw"
                src="/images/marketing/6862cc12e55703160e028aef_bg.webp"
              />
            </div>
          </div>

          {/* Right Column — Large image with floating overlay card */}
          <div className="relative">
            <div className="relative h-full min-h-[400px] w-full overflow-hidden rounded-xl lg:min-h-0">
                <Image
                  alt={t`CAD to render transformation example`}
                className="object-cover"
                fill
                loading="lazy"
                quality={85}
                sizes="(max-width: 768px) 100vw, 50vw"
                src="/images/marketing/6862cca34ad15f3dc365459f_bg-slide.webp"
              />
            </div>

            {/* Floating overlay card */}
            <div
              className="absolute bottom-4 left-4 right-4 rounded-xl bg-white p-5 shadow-lg sm:bottom-6 sm:left-6 sm:right-6 sm:p-6"
              data-testid="why-choose-card"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              {/* Arrow navigation at top of card */}
              <div className="mb-3 flex items-center justify-between">
                <div className="flex gap-2">
                  <button
                    aria-label={t`Previous slide`}
                    className="relative z-10 flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-gray-600 transition-colors hover:bg-gray-200 hover:text-gray-900"
                    onClick={goToPrevSlide}
                    type="button"
                  >
                    <svg
                      className="pointer-events-none h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      viewBox="0 0 24 24"
                    >
                      <path d="M15 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                  <button
                    aria-label={t`Next slide`}
                    className="relative z-10 flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-gray-600 transition-colors hover:bg-gray-200 hover:text-gray-900"
                    onClick={goToNextSlide}
                    type="button"
                  >
                    <svg
                      className="pointer-events-none h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      viewBox="0 0 24 24"
                    >
                      <path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                </div>

                {/* Dots at top-right of card */}
                <div className="flex gap-2">
                  {features.map((_, index) => (
                    <button
                      key={index}
                      aria-label={`${t`Show slide`} ${index + 1} / ${features.length}`}
                      aria-pressed={activeSlide === index}
                      className={`h-2.5 w-2.5 rounded-full transition-all ${
                        activeSlide === index
                          ? "bg-gray-900"
                          : "bg-gray-300 hover:bg-gray-400"
                      }`}
                      onClick={() => goToSlide(index)}
                      type="button"
                    />
                  ))}
                </div>
              </div>

              {/* Feature content */}
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <Image
                    alt={`${activeFeature.title} ${t`icon`}`}
                    className="h-12 w-12"
                    height={48}
                    src={activeFeature.icon}
                    width={48}
                  />
                </div>
                <div>
                  <h3 className="mb-1 text-lg font-semibold text-gray-900">
                    {activeFeature.title}
                  </h3>
                  <p className="text-sm leading-relaxed text-gray-600">
                    {activeFeature.description}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Full-width descriptive text below the grid */}
        <p className="mt-8 text-base leading-relaxed text-gray-600">
          {t`Upload your CAD drawings, sketches, or floor plans and watch AI transform them into professional renders. From concept to presentation, Aideco streamlines your visualization workflow.`}
        </p>
      </SectionContainer>
    </SectionShell>
  );
}
